import { SubscriptionTier, SubscriptionFeatures, Subscription, UserProfile } from '../types';

// NOTE: Stripe Price IDs are now defined in SubscriptionModal.tsx
// They are used directly when creating checkout sessions via Firebase Stripe Extension

// Define features for each tier
export const SUBSCRIPTION_FEATURES: Record<SubscriptionTier, SubscriptionFeatures> = {
  trial: {
    // Trial gets full S-Rank features for 14 days
    aiQuestGenerationsPerMonth: 'unlimited',
    aiProgramGenerationsPerMonth: 'unlimited',
    aiPriorityProcessing: true,
    maxActivePassiveSkills: 'unlimited',
    historyRetentionDays: 'unlimited',
    availableThemes: 'all',
    customThemeColors: true,
    dataExport: true,
    apiAccess: true,
    advancedAnalytics: true,
    monthlyCurrencyBonus: 1000,
    exclusiveShopItems: true,
    betaFeatures: true,
    supportPriority: 'vip',
  },
  e_rank: {
    aiQuestGenerationsPerMonth: 50,
    aiProgramGenerationsPerMonth: 5,
    aiPriorityProcessing: false,
    maxActivePassiveSkills: 10,
    historyRetentionDays: 180, // 6 months
    availableThemes: 3,
    customThemeColors: false,
    dataExport: false,
    apiAccess: false,
    advancedAnalytics: false,
    monthlyCurrencyBonus: 0,
    exclusiveShopItems: false,
    betaFeatures: false,
    supportPriority: 'priority',
  },
  s_rank: {
    aiQuestGenerationsPerMonth: 'unlimited',
    aiProgramGenerationsPerMonth: 'unlimited',
    aiPriorityProcessing: true,
    maxActivePassiveSkills: 'unlimited',
    historyRetentionDays: 'unlimited',
    availableThemes: 'all',
    customThemeColors: true,
    dataExport: true,
    apiAccess: true,
    advancedAnalytics: true,
    monthlyCurrencyBonus: 1000,
    exclusiveShopItems: true,
    betaFeatures: true,
    supportPriority: 'vip',
  },
  expired: {
    // Expired trial - no features available
    aiQuestGenerationsPerMonth: 0,
    aiProgramGenerationsPerMonth: 0,
    aiPriorityProcessing: false,
    maxActivePassiveSkills: 0,
    historyRetentionDays: 0,
    availableThemes: 0,
    customThemeColors: false,
    dataExport: false,
    apiAccess: false,
    advancedAnalytics: false,
    monthlyCurrencyBonus: 0,
    exclusiveShopItems: false,
    betaFeatures: false,
    supportPriority: 'community',
  },
};

export class SubscriptionService {
  /**
   * Get features for a subscription tier
   */
  getFeatures(tier: SubscriptionTier): SubscriptionFeatures {
    return SUBSCRIPTION_FEATURES[tier];
  }

  /**
   * Get current features for a user profile
   */
  getCurrentFeatures(profile: UserProfile): SubscriptionFeatures {
    return this.getFeatures(profile.subscription.tier);
  }

  /**
   * Check if subscription is active (not expired)
   */
  isSubscriptionActive(subscription: Subscription): boolean {
    const now = Date.now();
    
    // Check if trial period is active
    if (subscription.status === 'trialing' && subscription.trialEndsAt) {
      return now < subscription.trialEndsAt;
    }
    
    // Check if paid subscription is active
    if (subscription.status === 'active') {
      return now < subscription.currentPeriodEnd;
    }
    
    return false;
  }

  /**
   * Check if trial has expired
   */
  isTrialExpired(subscription: Subscription): boolean {
    if (subscription.tier !== 'trial' && subscription.tier !== 'expired') {
      return false; // Not on trial, so not expired
    }
    
    if (!subscription.trialEndsAt) {
      return false;
    }
    
    return Date.now() >= subscription.trialEndsAt;
  }

  /**
   * Get days remaining in trial
   */
  getTrialDaysRemaining(subscription: Subscription): number {
    if (!subscription.trialEndsAt) return 0;
    
    const now = Date.now();
    const remaining = subscription.trialEndsAt - now;
    
    if (remaining <= 0) return 0;
    
    // 🐛 FIX: Use Math.round for balanced countdown behavior
    // Math.ceil stayed at 7 days too long (up to 48 hours)
    // Math.floor showed 6 days immediately after signup
    // Math.round provides the best user experience:
    //   - Shows 7 days for new users (6.5+ days remaining)
    //   - Updates to 6 days after ~12-36 hours (standard countdown)
    return Math.round(remaining / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if user can use AI quest generation
   * 
   * ⚠️ SECURITY WARNING: This is CLIENT-SIDE ONLY validation!
   * TODO: Move to Cloud Function for server-side enforcement
   * Users can bypass this check via browser console
   */
  canUseAIQuestGeneration(profile: UserProfile): { allowed: boolean; remaining: number | 'unlimited'; reason?: string } {
    const features = this.getCurrentFeatures(profile);
    const subscription = profile.subscription;
    
    // Check if subscription is active (modal will handle expiration notification)
    if (!this.isSubscriptionActive(subscription)) {
      return {
        allowed: false,
        remaining: 0,
        reason: 'Your subscription has expired. Please subscribe to continue using AI features.',
      };
    }
    
    // Unlimited for S-Rank and Trial
    if (features.aiQuestGenerationsPerMonth === 'unlimited') {
      return { allowed: true, remaining: 'unlimited' };
    }
    
    // Check monthly limit
    const usage = this.getCurrentMonthUsage(profile);
    const used = usage.questGenerations;
    const limit = features.aiQuestGenerationsPerMonth as number;
    const remaining = Math.max(0, limit - used);
    
    if (used >= limit) {
      return {
        allowed: false,
        remaining: 0,
        reason: `You've reached your monthly limit of ${limit} AI quest generations. Upgrade to S-Rank for unlimited access.`,
      };
    }
    
    return { allowed: true, remaining };
  }

  /**
   * Check if user can use AI program generation
   * 
   * ⚠️ SECURITY WARNING: This is CLIENT-SIDE ONLY validation!
   * TODO: Move to Cloud Function for server-side enforcement
   * Users can bypass this check via browser console
   */
  canUseAIProgramGeneration(profile: UserProfile): { allowed: boolean; remaining: number | 'unlimited'; reason?: string } {
    const features = this.getCurrentFeatures(profile);
    const subscription = profile.subscription;
    
    // Check if subscription is active (modal will handle expiration notification)
    if (!this.isSubscriptionActive(subscription)) {
      return {
        allowed: false,
        remaining: 0,
        reason: 'Your subscription has expired. Please subscribe to continue using AI features.',
      };
    }
    
    // Unlimited for S-Rank and Trial
    if (features.aiProgramGenerationsPerMonth === 'unlimited') {
      return { allowed: true, remaining: 'unlimited' };
    }
    
    // Check monthly limit
    const usage = this.getCurrentMonthUsage(profile);
    const used = usage.programGenerations;
    const limit = features.aiProgramGenerationsPerMonth as number;
    const remaining = Math.max(0, limit - used);
    
    if (used >= limit) {
      return {
        allowed: false,
        remaining: 0,
        reason: `You've reached your monthly limit of ${limit} AI program generations. Upgrade to S-Rank for unlimited access.`,
      };
    }
    
    return { allowed: true, remaining };
  }

  /**
   * Check if user can add more passive skills
   */
  canAddPassiveSkill(profile: UserProfile, currentActiveCount: number): { allowed: boolean; reason?: string } {
    const features = this.getCurrentFeatures(profile);
    const subscription = profile.subscription;
    
    // Check if subscription is active
    if (!this.isSubscriptionActive(subscription)) {
      return {
        allowed: false,
        reason: 'Your trial has expired. Please subscribe to continue using passive skills.',
      };
    }
    
    if (features.maxActivePassiveSkills === 'unlimited') {
      return { allowed: true };
    }
    
    if (currentActiveCount >= (features.maxActivePassiveSkills as number)) {
      return {
        allowed: false,
        reason: `You've reached your limit of ${features.maxActivePassiveSkills} active passive skills. Upgrade to S-Rank for unlimited skills.`,
      };
    }
    
    return { allowed: true };
  }

  /**
   * Get current month key for AI usage tracking
   */
  private getCurrentMonthKey(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  
  /**
   * Get AI usage for current month (creates if doesn't exist)
   */
  private getCurrentMonthUsage(profile: UserProfile): { questGenerations: number; programGenerations: number } {
    const currentMonth = this.getCurrentMonthKey();
    const monthData = profile.aiUsage[currentMonth];
    
    if (!monthData) {
      // Initialize current month if it doesn't exist
      return { questGenerations: 0, programGenerations: 0 };
    }
    
    const usage = {
      questGenerations: monthData.questGenerations || 0,
      programGenerations: monthData.programGenerations || 0,
    };
    
    return usage;
  }

  /**
   * Track AI quest generation (call after successful generation)
   * Returns updated profile
   */
  trackQuestGeneration(profile: UserProfile): UserProfile {
    const currentMonth = this.getCurrentMonthKey();
    const currentUsage = this.getCurrentMonthUsage(profile);
    
    return {
      ...profile,
      aiUsage: {
        ...profile.aiUsage,
        [currentMonth]: {
          questGenerations: currentUsage.questGenerations + 1,
          programGenerations: currentUsage.programGenerations,
          lastUsed: Date.now(),
        },
      },
    };
  }

  /**
   * Track AI program generation (call after successful generation)
   * Returns updated profile
   */
  trackProgramGeneration(profile: UserProfile): UserProfile {
    const currentMonth = this.getCurrentMonthKey();
    const currentUsage = this.getCurrentMonthUsage(profile);
    
    return {
      ...profile,
      aiUsage: {
        ...profile.aiUsage,
        [currentMonth]: {
          questGenerations: currentUsage.questGenerations,
          programGenerations: currentUsage.programGenerations + 1,
          lastUsed: Date.now(),
        },
      },
    };
  }

  /**
   * Get accessible theme IDs for a user
   * E-Rank: First 3 themes (default, shadow, red)
   * S-Rank/Trial: All themes
   * Expired: Default only
   */
  getAccessibleThemes(profile: UserProfile): string[] {
    const features = this.getCurrentFeatures(profile);
    
    if (features.availableThemes === 'all') {
      // S-Rank and Trial get all themes
      return ['default', 'shadow', 'red', 'ice', 'gold', 'jeju'];
    } else if (typeof features.availableThemes === 'number') {
      // E-Rank gets first 3 themes
      return ['default', 'shadow', 'red'].slice(0, features.availableThemes);
    } else {
      // Expired gets only default
      return ['default'];
    }
  }

  /**
   * Check if user can access a specific theme
   */
  canAccessTheme(profile: UserProfile, themeId: string): { allowed: boolean; reason?: string } {
    const accessibleThemes = this.getAccessibleThemes(profile);
    
    if (accessibleThemes.includes(themeId)) {
      return { allowed: true };
    }
    
    const subscription = profile.subscription;
    if (!this.isSubscriptionActive(subscription)) {
      return {
        allowed: false,
        reason: 'Your trial has expired. Subscribe to access premium themes.',
      };
    }
    
    return {
      allowed: false,
      reason: 'Upgrade to S-Rank to unlock all themes.',
    };
  }

  /**
   * Create initial subscription for new users (14-day trial)
   */
  createTrialSubscription(testMode = false): Subscription {
    const now = Date.now();
    // 🧪 TEST MODE: Set trial to expire in 30 seconds for testing
    const trialDuration = testMode ? (30 * 1000) : (14 * 24 * 60 * 60 * 1000); // 30 seconds or 14 days
    const trialEndsAt = now + trialDuration;
    
    return {
      tier: 'trial',
      status: 'trialing',
      currentPeriodEnd: trialEndsAt,
      cancelAtPeriodEnd: false,
      trialEndsAt,
      createdAt: now,
      updatedAt: now,
    };
  }

  /**
   * Check if subscription has access to a specific tier requirement
   * Used for checking if user can access premium features that require specific tiers
   */
  hasSubscriptionAccess(subscription: Subscription, requiredTier: SubscriptionTier): boolean {
    // If no subscription or expired, no access
    if (!subscription || subscription.tier === 'expired') {
      return false;
    }
    
    // Check if subscription is active
    if (!this.isSubscriptionActive(subscription)) {
      return false;
    }
    
    // If required tier is trial, user must be on trial or higher
    if (requiredTier === 'trial') {
      return true; // All active subscriptions have at least trial level
    }
    
    // If required tier is e_rank, user must be e_rank or s_rank
    if (requiredTier === 'e_rank') {
      return subscription.tier === 'e_rank' || subscription.tier === 's_rank' || subscription.tier === 'trial';
    }
    
    // If required tier is s_rank, user must be s_rank or trial
    if (requiredTier === 's_rank') {
      return subscription.tier === 's_rank' || subscription.tier === 'trial';
    }
    
    return false;
  }

  /**
   * Get subscription tier display name
   */
  getTierDisplayName(tier: SubscriptionTier): string {
    const names: Record<SubscriptionTier, string> = {
      trial: '14-Day Trial',
      e_rank: 'E-Rank Hunter',
      s_rank: 'S-Rank Hunter',
      expired: 'Expired Trial',
    };
    return names[tier];
  }

  /**
   * Get subscription tier price
   */
  getTierPrice(tier: SubscriptionTier): { amount: number; displayPrice: string; originalPrice: string } | null {
    const prices = {
      e_rank: { amount: 2.99, displayPrice: '$2.99', originalPrice: '$5.99' },
      s_rank: { amount: 4.99, displayPrice: '$4.99', originalPrice: '$9.99' },
    };
    
    return prices[tier as keyof typeof prices] || null;
  }
}

export const subscriptionService = new SubscriptionService();

